import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  Aangifte,
  AangifteRapport,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {BehaviorSubject, filter, Observable, shareReplay, take} from 'rxjs';
import {FormControl, FormGroup} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box1-uitgaven-voor-inkomensvoorzieningen',
  templateUrl: './box1-uitgaven-voor-inkomensvoorzieningen.component.html',
  imports: [
    SharedUiFormControlsModule,
    CommonModule,
    SharedUiDetailsModule
  ],
  styleUrls: ['./box1-uitgaven-voor-inkomensvoorzieningen.component.scss']
})
export class Box1UitgavenVoorInkomensvoorzieningenComponent implements OnInit {

  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private bokkzClient = inject(IbRekenService);
  private rapportRequestId = 0;
  public dataVisible$ = new BehaviorSubject<boolean>(false);
  public year: any;

  public readonly ruimteJaren: { offset: number; suffix: string }[] = [
    { offset: 1,  suffix: 'MinEen' },
    { offset: 2,  suffix: 'MinTwee' },
    { offset: 3,  suffix: 'MinDrie' },
    { offset: 4,  suffix: 'MinVier' },
    { offset: 5,  suffix: 'MinVijf' },
    { offset: 6,  suffix: 'MinZes' },
    { offset: 7,  suffix: 'MinZeven' },
    { offset: 8,  suffix: 'MinAcht' },
    { offset: 9,  suffix: 'MinNegen' },
    { offset: 10, suffix: 'MinTien' },
  ];

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    aftrekbareLijfrentePremie: new FormControl<number | null>(0),
    jaarruimte: new FormControl<number | null>(0),
    reserveringsruimte: new FormControl<number | null>(0),
    premieGrondslag: new FormControl<number | null>(0),
    reserveringsruimteMinEen: new FormControl<number | null>(0),
    reserveringsruimteMinTwee: new FormControl<number | null>(0),
    reserveringsruimteMinDrie: new FormControl<number | null>(0),
    reserveringsruimteMinVier: new FormControl<number | null>(0),
    reserveringsruimteMinVijf: new FormControl<number | null>(0),
    reserveringsruimteMinZes: new FormControl<number | null>(0),
    reserveringsruimteMinZeven: new FormControl<number | null>(0),
    reserveringsruimteMinAcht: new FormControl<number | null>(0),
    reserveringsruimteMinNegen: new FormControl<number | null>(0),
    reserveringsruimteMinTien: new FormControl<number | null>(0),
    betaaldePremiesVoorLijfrente: new FormControl<number | null>(0),
    benutteJaarrruimte: new FormControl<number | null>(0),
    resterendeJaarruimte: new FormControl<number | null>(0),
    benutteReserveringsruimte: new FormControl<number | null>(0),
    benutteReserveringsruimteMinEen: new FormControl<number | null>(0),
    benutteReserveringsruimteMinTwee: new FormControl<number | null>(0),
    benutteReserveringsruimteMinDrie: new FormControl<number | null>(0),
    benutteReserveringsruimteMinVier: new FormControl<number | null>(0),
    benutteReserveringsruimteMinVijf: new FormControl<number | null>(0),
    benutteReserveringsruimteMinZes: new FormControl<number | null>(0),
    benutteReserveringsruimteMinZeven: new FormControl<number | null>(0),
    benutteReserveringsruimteMinAcht: new FormControl<number | null>(0),
    benutteReserveringsruimteMinNegen: new FormControl<number | null>(0),
    benutteReserveringsruimteMinTien: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinEen: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinTwee: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinDrie: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinVier: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinVijf: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinZes: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinZeven: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinAcht: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinNegen: new FormControl<number | null>(0),
    resterendeReserveringsruimteMinTien: new FormControl<number | null>(0),
    saldoResterendeRuimtes: new FormControl<number | null>(0),
    resterendeTotaleRuimte: new FormControl<number | null>(0),
    lijfrenteVoorKind: new FormControl<number | null>(0),
    premieAovVerzekering: new FormControl<number | null>(0),
    premieAnwVerzekering: new FormControl<number | null>(0),
    premieOmzettingForBijStaking: new FormControl<number | null>(0),
    premieAfstortingFor: new FormControl<number | null>(0),
    premieOmzettingStakingsWinstInLijfrente: new FormControl<number | null>(0),
    saldoPremiesInkomensvoorzieningen: new FormControl<number | null>(0),
    saldoNegatievePremiesInkomensvoorzieningen: new FormControl<number | null>(0),
    alGebruikteRuimteMinEen: new FormControl<number | null>(0),
    alGebruikteRuimteMinTwee: new FormControl<number | null>(0),
    alGebruikteRuimteMinDrie: new FormControl<number | null>(0),
    alGebruikteRuimteMinVier: new FormControl<number | null>(0),
    alGebruikteRuimteMinVijf: new FormControl<number | null>(0),
    alGebruikteRuimteMinZes: new FormControl<number | null>(0),
    alGebruikteRuimteMinZeven: new FormControl<number | null>(0),
    alGebruikteRuimteMinAcht: new FormControl<number | null>(0),
    alGebruikteRuimteMinNegen: new FormControl<number | null>(0),
    alGebruikteRuimteMinTien: new FormControl<number | null>(0),
    emptyField: new FormControl<number>(0),
  });

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entity => {
      this.year = entity.year!;
    });
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((isPartner) => {
      const aangifte = this.dataService.incomeTaxEntity.value.aangifte;
      if (!aangifte) {
        this.dataVisible$.next(false);
        return;
      }
      this.getRapportIfChanged(aangifte, isPartner);
    });
  }

  public getRapportIfChanged(aangifte: Aangifte, partner : boolean) {
    const requestId = ++this.rapportRequestId;
    this.dataVisible$.next(false);

    if (partner) {
      this.bokkzClient.getIncomeTaxReport(aangifte.partnerAangifte!).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      })
    }
    else {
      this.bokkzClient.getIncomeTaxReport(aangifte).subscribe(rapport => {
        if (requestId !== this.rapportRequestId) {
          return;
        }
        this.resetAndPatchForms(rapport);
      });
    }
  }

  private resetAndPatchForms(details: AangifteRapport): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.formGroup.patchValue(details.specificatieLijfrente?.opbouw ?? {}, {emitEvent: false});
    this.formGroup.patchValue({saldoNegatievePremiesInkomensvoorzieningen : details.specificatieInkomenBox1?.opbouw?.negatieveUitgavenVoorInkomensvoorzieningen}, {emitEvent: false});


    if (this.formGroup.controls.benutteJaarrruimte.value == null) {
      this.formGroup.controls.benutteJaarrruimte.setValue(0, {emitEvent: false});
    }

    const jaarruimte = this.formGroup.controls.jaarruimte.value ?? 0;
    const benutteJaarrruimte = this.formGroup.controls.benutteJaarrruimte.value ?? 0;
    this.formGroup.controls.emptyField.setValue(0, {emitEvent: false});
    this.formGroup.controls.resterendeJaarruimte.setValue(jaarruimte - benutteJaarrruimte, {emitEvent: false});

    const controls = this.formGroup.controls as Record<string, { value: number | null; setValue(v: number, opts: object): void }>;

    this.ruimteJaren.forEach(jaar => {
      const alGebruikteControl = controls['alGebruikteRuimte' + jaar.suffix];
      if (alGebruikteControl?.value == null) {
        alGebruikteControl.setValue(0, {emitEvent: false});
      }

      const reserveringsruimte = controls['reserveringsruimte' + jaar.suffix]?.value ?? 0;
      const alGebruikteRuimte  = controls['alGebruikteRuimte' + jaar.suffix]?.value ?? 0;
      const benutteRuimte      = controls['benutteReserveringsruimte' + jaar.suffix]?.value ?? 0;
      const resterendeControl  = controls['resterendeReserveringsruimte' + jaar.suffix];

      if (resterendeControl) {
        resterendeControl.setValue(reserveringsruimte - alGebruikteRuimte - benutteRuimte, {emitEvent: false});
      }
    });

    // resterendeTotaleRuimte = sum of all resterendeReserveringsruimte* values
    const resterendeTotaal = this.ruimteJaren.reduce((sum, jaar) => {
      return sum + (controls['resterendeReserveringsruimte' + jaar.suffix]?.value ?? 0);
    }, 0);
    this.formGroup.controls.resterendeTotaleRuimte.setValue(resterendeTotaal, {emitEvent: false});

    this.formGroup.disable({emitEvent: false});
    this.dataVisible$.next(true);
  }

  calculateAftrekbarePremies(): any {
    const sum = this.aftrekbarePremiesTotaal;
    this.formGroup.patchValue({ saldoPremiesInkomensvoorzieningen: sum }, { emitEvent: false });
    return sum;
  }

  get aftrekbarePremiesTotaal(): number {
    const aftrekbareLijfrentePremie = this.formGroup.controls.aftrekbareLijfrentePremie.value ?? 0;
    const lijfrenteVoorKind = this.formGroup.controls.lijfrenteVoorKind.value ?? 0;
    const premieAovVerzekering = this.formGroup.controls.premieAovVerzekering.value ?? 0;
    const premieAnwVerzekering = this.formGroup.controls.premieAnwVerzekering.value ?? 0;
    const negatievePremies = this.formGroup.controls.saldoNegatievePremiesInkomensvoorzieningen.value ?? 0;
    const sumFromParts = aftrekbareLijfrentePremie + lijfrenteVoorKind + premieAovVerzekering + premieAnwVerzekering - negatievePremies;

    const patchedSaldo = this.formGroup.controls.saldoPremiesInkomensvoorzieningen.value;
    if (patchedSaldo == null) {
      return sumFromParts;
    }

    // If backend saldo is 0 but detailed values have data, prefer the detailed sum.
    if (patchedSaldo === 0 && sumFromParts > 0) {
      return sumFromParts;
    }

    return patchedSaldo;
  }

  hasReserveringsruimte(): boolean {
    return this.ruimteJaren.some(jaar => this.hasReserveringsruimteVoorJaar(jaar.offset));
  }

  hasReserveringsruimteVoorJaar(offset: number): boolean {
    const suffix = this.ruimteJaren.find(j => j.offset === offset)?.suffix;
    if (!suffix) return false;
    const key = ('reserveringsruimte' + suffix) as keyof typeof this.formGroup.controls;
    return (this.formGroup.controls[key]?.value ?? null) !== null;
  }
}
